/**
 * Logged-out entry screen: login / register / forgot-password modes. No
 * bottom nav (see app.jsx NAV_VISIBLE). On login/register success,
 * onAuthenticated() from store.js sets the session and navigates home —
 * this component never navigates itself. The forgot mode ends in a
 * confirmation showing the MASKED address the mail went to ("E-mail
 * verzonden naar d***@g***.nl") — with no email verification at
 * registration, "ging het naar het juiste adres?" is exactly the doubt
 * that view exists to settle. `params.mode === 'forgot'` opens that mode
 * directly (used by ResetPasswordScreen's "request a new link" path).
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api.js';
import { onAuthenticated, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import RecaptchaNotice from '../components/RecaptchaNotice.jsx';
import {
  preloadRecaptcha,
  getRecaptchaToken,
  recaptchaScriptFailed,
} from '../lib/recaptcha.js';

const EMPTY_LOGIN = { username: '', password: '' };
const EMPTY_REGISTER = { username: '', password: '', email: '', website: '' };
const EMPTY_FORGOT = { login: '', website: '' };

export default function AuthScreen({ params }) {
  // 'login' | 'register' | 'forgot'
  const [mode, setMode] = useState(params?.mode === 'forgot' ? 'forgot' : 'login');
  const [loginFields, setLoginFields] = useState(EMPTY_LOGIN);
  const [registerFields, setRegisterFields] = useState(EMPTY_REGISTER);
  const [forgotFields, setForgotFields] = useState(EMPTY_FORGOT);
  // Masked address from a successful forgot request; non-null swaps the
  // forgot form for the confirmation view.
  const [sentTo, setSentTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Warm the reCAPTCHA script (no-op when unconfigured) so the first
  // submit isn't slowed by the download + Google's readiness handshake.
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // The backend rejects auth without a valid token when reCAPTCHA is
  // configured; when we know the script itself never loaded (adblocker),
  // name the real problem instead of echoing the generic rejection.
  function authError(err) {
    if (err && err.code === 'etm_recaptcha_failed' && recaptchaScriptFailed()) {
      return __('De beveiligingscontrole (reCAPTCHA) kon niet geladen worden. Schakel je adblocker uit voor deze site en probeer het opnieuw.', 'etm');
    }
    return friendlyError(err);
  }

  function switchMode(next) {
    setMode(next);
    setError('');
    setSentTo(null);
  }

  function updateLogin(field, value) {
    setLoginFields((prev) => ({ ...prev, [field]: value }));
  }

  function updateRegister(field, value) {
    setRegisterFields((prev) => ({ ...prev, [field]: value }));
  }

  function updateForgot(field, value) {
    setForgotFields((prev) => ({ ...prev, [field]: value }));
  }

  async function handleLogin(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('login');
      const res = await api.auth.login({
        username: loginFields.username,
        password: loginFields.password,
        recaptcha_token: recaptchaToken,
      });
      await onAuthenticated(res);
    } catch (err) {
      const message = authError(err);
      setError(message);
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('register');
      const res = await api.auth.register({
        username: registerFields.username,
        password: registerFields.password,
        email: registerFields.email,
        website: registerFields.website,
        recaptcha_token: recaptchaToken,
      });
      await onAuthenticated(res);
    } catch (err) {
      const message = authError(err);
      setError(message);
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgot(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('forgot');
      const res = await api.auth.forgotPassword({
        login: forgotFields.login,
        website: forgotFields.website,
        recaptcha_token: recaptchaToken,
      });
      setSentTo(res.masked_email || '');
    } catch (err) {
      const message = authError(err);
      setError(message);
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="etm-login-container">
      <div class="etm-login-logo">ETM</div>
      <p class="etm-login-subtitle">{__('Efficiënte Thuis Manager', 'etm')}</p>

      {mode === 'login' ? (
        <form onSubmit={handleLogin}>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-login-username">
              {__('Gebruikersnaam', 'etm')}
            </label>
            <input
              id="etm-login-username"
              class="etm-form-input"
              type="text"
              autoComplete="username"
              required
              value={loginFields.username}
              onInput={(e) => updateLogin('username', e.currentTarget.value)}
            />
          </div>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-login-password">
              {__('Wachtwoord', 'etm')}
            </label>
            <input
              id="etm-login-password"
              class="etm-form-input"
              type="password"
              autoComplete="current-password"
              required
              value={loginFields.password}
              onInput={(e) => updateLogin('password', e.currentTarget.value)}
            />
          </div>

          {error ? <p class="etm-form-error">{error}</p> : null}

          <button type="submit" class="etm-btn etm-btn-primary" disabled={submitting}>
            {submitting ? __('Bezig…', 'etm') : __('Inloggen', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            disabled={submitting}
            onClick={() => switchMode('register')}
          >
            {__('Account aanmaken', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-ghost"
            disabled={submitting}
            onClick={() => switchMode('forgot')}
          >
            {__('Wachtwoord vergeten?', 'etm')}
          </button>

          <RecaptchaNotice />
        </form>
      ) : mode === 'register' ? (
        <form onSubmit={handleRegister}>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-register-username">
              {__('Gebruikersnaam', 'etm')}
            </label>
            <input
              id="etm-register-username"
              class="etm-form-input"
              type="text"
              autoComplete="username"
              required
              value={registerFields.username}
              onInput={(e) => updateRegister('username', e.currentTarget.value)}
            />
          </div>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-register-email">
              {__('E-mailadres', 'etm')}
            </label>
            <input
              id="etm-register-email"
              class="etm-form-input"
              type="email"
              autoComplete="email"
              required
              value={registerFields.email}
              onInput={(e) => updateRegister('email', e.currentTarget.value)}
            />
          </div>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-register-password">
              {__('Wachtwoord', 'etm')}
            </label>
            <input
              id="etm-register-password"
              class="etm-form-input"
              type="password"
              autoComplete="new-password"
              required
              value={registerFields.password}
              onInput={(e) => updateRegister('password', e.currentTarget.value)}
            />
          </div>
          <p class="etm-field-hint">{__('Minimaal 8 tekens.', 'etm')}</p>

          {/* Honeypot — visually hidden, never filled in by a real user. */}
          <div
            class="etm-honeypot-field"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
            aria-hidden="true"
          >
            <label for="etm-register-website">{__('Website', 'etm')}</label>
            <input
              id="etm-register-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={registerFields.website}
              onInput={(e) => updateRegister('website', e.currentTarget.value)}
            />
          </div>

          {error ? <p class="etm-form-error">{error}</p> : null}

          <button type="submit" class="etm-btn etm-btn-primary" disabled={submitting}>
            {submitting ? __('Bezig…', 'etm') : __('Account aanmaken', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            disabled={submitting}
            onClick={() => switchMode('login')}
          >
            {__('Inloggen', 'etm')}
          </button>

          <RecaptchaNotice />
        </form>
      ) : sentTo !== null ? (
        <div class="etm-forgot-confirmation">
          <p class="etm-screen-subtitle">
            {__('E-mail verzonden naar %s', 'etm').replace('%s', sentTo)}
          </p>
          <p class="etm-field-hint">
            {__('Geen mail ontvangen? Kijk ook in je spamfolder. De link is 24 uur geldig.', 'etm')}
          </p>
          <button
            type="button"
            class="etm-btn etm-btn-primary"
            onClick={() => switchMode('login')}
          >
            {__('Terug naar inloggen', 'etm')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleForgot}>
          <p class="etm-field-hint">
            {__('Vul je gebruikersnaam of e-mailadres in; je ontvangt een link om een nieuw wachtwoord in te stellen.', 'etm')}
          </p>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-forgot-login">
              {__('Gebruikersnaam of e-mailadres', 'etm')}
            </label>
            <input
              id="etm-forgot-login"
              class="etm-form-input"
              type="text"
              autoComplete="username"
              required
              value={forgotFields.login}
              onInput={(e) => updateForgot('login', e.currentTarget.value)}
            />
          </div>

          {/* Honeypot — same field name and treatment as the register form. */}
          <div
            class="etm-honeypot-field"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
            aria-hidden="true"
          >
            <label for="etm-forgot-website">{__('Website', 'etm')}</label>
            <input
              id="etm-forgot-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={forgotFields.website}
              onInput={(e) => updateForgot('website', e.currentTarget.value)}
            />
          </div>

          {error ? <p class="etm-form-error">{error}</p> : null}

          <button type="submit" class="etm-btn etm-btn-primary" disabled={submitting}>
            {submitting ? __('Bezig…', 'etm') : __('Verstuur herstellink', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            disabled={submitting}
            onClick={() => switchMode('login')}
          >
            {__('Terug naar inloggen', 'etm')}
          </button>

          <RecaptchaNotice />
        </form>
      )}
    </div>
  );
}
